import { useContext } from "react";
import { Button } from "@/components/ui/button";
import { useApiClient } from "@/context/ApiContext";
import { CurrentUserContext } from "@/context/CurrentUserContext";
import { useServiceRequestForm } from "./hooks/useServiceRequestForm";
import { SuccessScreen } from "./components/SuccessScreen";
import { ServiceTypeSection } from "./components/ServiceTypeSection";
import { ClientSection } from "./components/ClientSection";
import { StaffSearchSection } from "./components/StaffSearchSection";
import { StaffingDetailsSection } from "./components/StaffingDetailsSection";
import { ClientScheduleSection } from "./components/ClientScheduleSection";
import { SubmitArea } from "./components/SubmitArea";

interface ServiceRequestFormProps {
  onBack?: () => void;
}

export default function ServiceRequestForm({ onBack }: ServiceRequestFormProps) {
  const apiClient = useApiClient();
  const { currentUser, isLoading: userLoading } = useContext(CurrentUserContext);

  const form = useServiceRequestForm({ apiClient, currentUser, userLoading, onBack });

  if (form.submitSuccess) {
    return (
      <SuccessScreen
        selectedStaff={form.selectedStaff}
        selectedClient={form.selectedClient}
        aspModalOpen={form.aspModalOpen}
        onAspModalOpenChange={form.setAspModalOpen}
        aspWorkflowResults={form.aspWorkflowResults}
        scheduleMessage={form.scheduleMessage}
        onReset={form.handleReset}
        onBack={onBack}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-6">
      {onBack && (
        <Button
          variant="ghost"
          size="sm"
          className="self-start -mb-2"
          onClick={onBack}
          disabled={form.saving}
        >
          ← Back
        </Button>
      )}

      <ServiceTypeSection
        serviceType={form.serviceType}
        onServiceTypeChange={form.handleServiceTypeChange}
        error={form.errors.serviceType}
        disabled={form.saving}
      />

      {form.hasServiceType && (
        <ClientSection
          clients={form.clients}
          loadingClients={form.loadingClients}
          selectedClient={form.selectedClient}
          onClientChange={form.handleClientChange}
          error={form.errors.clientId}
          disabled={form.saving}
        />
      )}

      {form.isAddProvider && (
        <StaffSearchSection
          staffSearchQuery={form.staffSearchQuery}
          staffResults={form.staffResults}
          selectedStaff={form.selectedStaff}
          staffSearchLoading={form.staffSearchLoading}
          onSearchChange={form.handleStaffSearchChange}
          onStaffSelect={form.handleStaffSelect}
          onClearStaff={form.resetStaffSearch}
          error={form.errors.staffUser}
          disabled={form.saving}
        />
      )}

      {form.isNeedHelp && (
        <StaffingDetailsSection
          clientService={form.clientService}
          onClientServiceChange={form.setClientService}
          language={form.language}
          onLanguageChange={form.setLanguage}
          clientServiceError={form.errors.clientService}
          languageError={form.errors.language}
          disabled={form.saving}
        />
      )}

      {form.hasServiceType && (
        <ClientScheduleSection
          clientSchedule={form.clientSchedule}
          onClientScheduleChange={form.setClientSchedule}
          clientSelected={form.selectedClient !== null}
          isAddProvider={form.isAddProvider}
          error={form.errors.clientSchedule}
          disabled={form.saving}
        />
      )}

      <SubmitArea
        saving={form.saving}
        submitError={form.submitError}
        hasServiceType={form.hasServiceType}
        isAddProvider={form.isAddProvider}
        submitBlockedReason={form.submitBlockedReason}
        onSubmit={form.handleSubmit}
        onReset={form.handleReset}
        onBack={onBack}
      />
    </div>
  );
}
